import React, { useState, useEffect } from 'react';

const sections = [
  { id: 'abstract', label: 'Abstract' },
  { id: 'motivation', label: 'Motivation' },
  { id: 'ssah', label: 'SSAH' },
  { id: 'sah', label: 'SAH' },
  { id: 'lessismore', label: 'Less Is More' },
  { id: 'discussion', label: 'Discussion' },
  { id: 'citation', label: 'BibTeX' },
];

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [scrolled, setScrolled] = useState(false);

  const handleScroll = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const headerHeight = document.querySelector('header').offsetHeight;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = window.scrollY + elementPosition - headerHeight - 8;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleScrollEvent = () => {
      setScrolled(window.scrollY > 4);
      const headerHeight = document.querySelector('header').offsetHeight;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= headerHeight + 16 && rect.bottom >= headerHeight + 16) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    handleScrollEvent();
    window.addEventListener('scroll', handleScrollEvent, { passive: true });
    return () => window.removeEventListener('scroll', handleScrollEvent);
  }, []);

  return (
    <header
      className={`sticky top-0 z-30 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-md bg-white/75 border-b border-ink-100 shadow-soft'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between py-3 px-5 md:px-8">
        <button
          className="flex items-center gap-2 group focus:outline-none"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-grad-brand-accent text-white font-bold text-sm shadow-sm">
            S
          </span>
          <span className="text-base md:text-lg font-semibold tracking-tight text-ink-900 group-hover:text-brand-600 transition-colors">
            SSAH
          </span>
          <span className="hidden lg:inline text-sm text-ink-400 font-medium ml-1">
            · Superficial Safety Alignment Hypothesis
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-1 relative">
          {sections.map((section) => {
            const isActive = activeSection === section.id;
            return (
              <button
                key={section.id}
                onClick={() => handleScroll(section.id)}
                className={`relative px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? 'text-brand-700'
                    : 'text-ink-500 hover:text-ink-900'
                }`}
              >
                {section.label}
                {isActive && (
                  <span className="absolute left-2 right-2 -bottom-0.5 h-[2px] rounded-full bg-grad-brand-accent-r" />
                )}
              </button>
            );
          })}
        </nav>

        <button
          className="md:hidden text-ink-700 focus:outline-none p-2 -mr-2"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d={isMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16m-7 6h7'}
            />
          </svg>
        </button>
      </div>

      {isMenuOpen && (
        <nav className="md:hidden bg-white/95 backdrop-blur border-t border-ink-100">
          <ul className="flex flex-col py-2">
            {sections.map((section) => {
              const isActive = activeSection === section.id;
              return (
                <li key={section.id}>
                  <button
                    onClick={() => handleScroll(section.id)}
                    className={`w-full text-left px-6 py-2.5 text-sm transition-colors ${
                      isActive
                        ? 'text-brand-700 font-semibold bg-brand-50/60'
                        : 'text-ink-600 hover:bg-ink-50'
                    }`}
                  >
                    {section.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      )}
    </header>
  );
}

export default Header;
